import type { ColDef } from "@performyard/py-components";

import type { DecisionRow } from "../../types";

import {
	BudgetReviewRowEditCellRenderer,
	CurrentSalaryCellRenderer,
	EmployeeAvatarCellRenderer,
	EmployeeInfoCellRenderer,
	EmployeeInfoCellRendererBudgetReviews,
	IncreaseAmountCellRenderer,
	IncreasePercentCellRenderer,
	NewSalaryCellRenderer,
	SuggestedIncreaseCellRenderer,
} from "../CellRenderers";

/** 4px horizontal padding on each cell/header cell so adjacent columns are separated by 8px. */
export const DECISION_TABLE_COLUMN_GAP_CLASSNAME = "[&_.ag-cell]:!px-1 [&_.ag-header-cell]:!px-1";

/** Applied to Employee Info column cell/header in budget review read-only defs (keep in sync with {@link BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME}). */
export const BUDGET_REVIEW_EMPLOYEE_INFO_CELL_CLASSNAME = "budget-review-employee-info-cell";

/**
 * Budget review read-only grid: 16px inset left of the Employee column and right of Employee Info
 * (selectors beat {@link DECISION_TABLE_COLUMN_GAP_CLASSNAME} and Table.css first/last padding).
 */
export const BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME =
	"[&_.ag-cell.ag-column-first]:!pl-4 [&_.ag-header-cell.ag-column-first]:!pl-4 [&_.ag-cell.budget-review-employee-info-cell]:!pr-4 [&_.ag-header-cell.budget-review-employee-info-cell]:!pr-4";

export const READ_ONLY_TABLE_CLASSNAME = `border-t border-solid border-neutral-2 bg-neutral-0 [&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none ${DECISION_TABLE_COLUMN_GAP_CLASSNAME} ${BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME}`;

export const EDITABLE_TABLE_CLASSNAME = `[&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none ${DECISION_TABLE_COLUMN_GAP_CLASSNAME}`;

export const BUDGET_REVIEW_TABLE_CLASSNAME = `min-w-0 max-w-full border-t border-solid border-neutral-2 bg-neutral-0 [&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none ${DECISION_TABLE_COLUMN_GAP_CLASSNAME} ${BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME}`;

// --- Shared column definitions ---

const employeeCol: ColDef<DecisionRow> = {
	headerName: "Employee",
	field: "employee_name",
	cellRenderer: EmployeeAvatarCellRenderer,
	width: 200,
	flex: 1.5,
	sortable: true,
};

const currentSalaryCol: ColDef<DecisionRow> = {
	headerName: "Current Salary",
	field: "current_salary",
	cellRenderer: CurrentSalaryCellRenderer,
	width: 105,
	flex: 1,
	sortable: true,
};

const suggestedCol: ColDef<DecisionRow> = {
	headerName: "Suggested",
	sortable: false,
	width: 105,
	flex: 1,
	cellRenderer: SuggestedIncreaseCellRenderer,
};

const increasePercentCol: ColDef<DecisionRow> = {
	headerName: "Increase",
	field: "proposed_increase_percent",
	cellRenderer: IncreasePercentCellRenderer,
	width: 105,
	flex: 1,
	sortable: false,
};

const increaseAmountCol: ColDef<DecisionRow> = {
	headerName: "Increase",
	field: "proposed_increase_amount",
	width: 105,
	cellRenderer: IncreaseAmountCellRenderer,
	flex: 1,
	sortable: false,
};

const newSalaryCol: ColDef<DecisionRow> = {
	headerName: "New Salary",
	field: "proposed_salary",
	cellRenderer: NewSalaryCellRenderer,
	flex: 1,
	sortable: true,
};

export const expandedDecisionTableColumnDefs: ColDef<DecisionRow>[] = [
	employeeCol,
	currentSalaryCol,
	suggestedCol,
	{
		headerName: "Suggested",
		width: 105,
		flex: 0.8,
		sortable: false,
		cellRenderer: SuggestedIncreaseCellRenderer,
	},
	increasePercentCol,
	increaseAmountCol,
	{
		headerName: "Employee Info",
		cellRenderer: EmployeeInfoCellRenderer,
		width: 105,
		sortable: false,
		resizable: false,
	},
	newSalaryCol,
];

/** Read-only budget review: salary columns plus Employee Info (same three actions as My Decisions). */
export const budgetReviewReadOnlySalaryColumnDefs: ColDef<DecisionRow>[] = [
	employeeCol,
	currentSalaryCol,
	suggestedCol,
	increasePercentCol,
	increaseAmountCol,
	{ ...newSalaryCol, width: 105 },
	{
		headerName: "Employee Info",
		colId: "budgetReviewEmployeeInfo",
		cellClass: BUDGET_REVIEW_EMPLOYEE_INFO_CELL_CLASSNAME,
		headerClass: BUDGET_REVIEW_EMPLOYEE_INFO_CELL_CLASSNAME,
		cellRenderer: EmployeeInfoCellRendererBudgetReviews,
		width: 105,
		minWidth: 124,
		flex: 0,
		sortable: false,
		resizable: false,
	},
	{
		headerName: "",
		colId: "budgetReviewReadOnlyBlank",
		width: 32,
		flex: 0,
		sortable: false,
		resizable: false,
	},
];

/** Cycle must be active (not scheduled or completed) and budgets must be - at least - submited */
export const budgetReviewManagerGroupStatusShowsRowEdit = (status: string, cycleStatus: string): boolean =>
	cycleStatus === "active" && (status === "in_review" || status === "approved");

/** Same as {@link budgetReviewReadOnlySalaryColumnDefs} plus a trailing Edit column (submitted/approved groups). */
export const budgetReviewSalaryColumnDefsWithEdit: ColDef<DecisionRow>[] = [
	...budgetReviewReadOnlySalaryColumnDefs,
	{
		headerName: "",
		colId: "budgetReviewRowEdit",
		width: 80,
		minWidth: 80,
		flex: 0,
		sortable: false,
		resizable: false,
		cellClass: "!overflow-visible",
		cellRenderer: BudgetReviewRowEditCellRenderer,
	},
];
